import { Button, ButtonGroup } from "@mui/material";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { CommodityManagementApi, MarkItViewApi } from "../../../../Apis/Apis";
import {
  BudgetPricingComponentModel,
  BudgetPricingStrategyModel,
  GetCustomerDataResponse,
  GetCustomerSettingsResponse,
  PositionPricingComponentModel,
} from "../../../../Generated/Commodity-Management-Api";
import { useLoadingState } from "../../../../Hooks";
import { RootState, useApplicationSelector } from "../../../../Redux/ReduxStore";
import { LoadingState } from "../../../../Types";
import { StoneXGrid, StoneXLoading, StoneXRow } from "../../../StoneX";
import { StoneXModal } from "../../../StoneXMui";
import { MarketViewProductModel } from "../../../../Generated/Mark-It-View-Api";
import { useSnackbar } from "notistack";
import PricingStrategyEditor, {
  createEmptyBudgetPricingStrategy,
  savePricingStrategy,
} from "./PricingStrategyEditor";

type GridRowType = "budget" | "position";

interface PricingStrategyGridRow {
  id: string;
  path: string[];
  rowType: GridRowType;
  budgetPricingStrategyId: number;
  positionPricingStrategyId?: number;
  name: string;
  commodityLabel: string;
  locationLabel: string;
  counterpartyLabel: string;
  bushelUnit: string;
  bushelVolume: string;
  contractUnit: string;
  contractVolume: string;
  electricityType: string;
  componentDetails: string[];
}

export default function PricingStrategySettings() {
  const loadingState = useLoadingState();
  const { enqueueSnackbar } = useSnackbar();

  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

  const usesSeparatePricingStrategies = customerSettings?.separatePricingStrategiesForPositions ?? false;
  const showLocationColumn = (customerSettings?.budgetsByLocation ?? false) || (usesSeparatePricingStrategies && customerSettings?.positionsByLocation);
  const showCounterpartyColumn = (customerSettings?.budgetsByCounterparty ?? false) || (usesSeparatePricingStrategies && customerSettings?.positionsByCounterparty);

  const [marketViewProducts, setMarketViewProducts] = useState<MarketViewProductModel[]>([]);
  const [budgetPricingStrategies, setBudgetPricingStrategies] = useState<BudgetPricingStrategyModel[]>([]);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [modalStrategy, setModalStrategy] = useState<BudgetPricingStrategyModel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  async function refreshPricingStrategies() {
    const budgetResponse = await CommodityManagementApi.listPricingStrategies(undefined);
    setBudgetPricingStrategies(budgetResponse.data.rows ?? []);
  }

  useEffect(() => {
    async function getPricingStrategies() {
      try {
        loadingState.setLoadingState(LoadingState.Loading);
        const [budgetResponse, marketViewResponse] = await Promise.all([
          CommodityManagementApi.listPricingStrategies(undefined),
          MarkItViewApi.listMarketViewProducts(undefined),
        ]);

        setBudgetPricingStrategies(budgetResponse.data.rows ?? []);
        setMarketViewProducts(marketViewResponse.data.rows ?? []);

        loadingState.setLoadingState(LoadingState.Loaded);
      } catch {
        loadingState.setLoadingState(LoadingState.Failed);
      }
    }

    getPricingStrategies();
  }, []);

  const commodityNameById = useMemo(
    () => new Map((customerData.commodities ?? []).map((x) => [x.commodityId, x.commodityName])),
    [customerData.commodities],
  );

  const locationNameById = useMemo(
    () => new Map((customerData.locations ?? []).map((x) => [x.locationId, x.locationName])),
    [customerData.locations],
  );
  
  const counterpartyNameById = useMemo(
    () => new Map((customerData.counterparties ?? []).map((x) => [x.counterpartyId, x.counterpartyName])),
    [customerData.counterparties],
  );

  const marketViewProductNameById = useMemo(
    () => new Map(marketViewProducts.map((x) => [x.productId, x.name])),
    [marketViewProducts],
  );

  function getScopeLabel(id: number | null | undefined, byId: Map<number, string>, allLabel: string) {
    if (id === null || id === undefined) {
      return allLabel;
    }

    return byId.get(id) ?? `${id}`;
  }

  function getComponentDetails(component: BudgetPricingComponentModel | PositionPricingComponentModel, productNameById: Map<number, string>) {
    const details: string[] = [];

    if (component.marketPriceProvider !== undefined && component.marketPriceProvider !== null) {
      details.push(component.marketPriceProvider);
    }

    if (component.marketViewProductId !== undefined && component.marketViewProductId !== null) {
      const productName = productNameById.get(component.marketViewProductId);
      details.push(productName ?? `${component.marketViewProductId}`);
    }

    if ("settlementSymbol" in component && component.settlementSymbol) {
      details.push(`Settlement: ${component.settlementSymbol}`);
    }

    if ("bushelUnit" in component && component.bushelUnit) {
      details.push(`Bushel: ${component.bushelUnit}${component.bushelVolume !== null && component.bushelVolume !== undefined ? ` ${component.bushelVolume}` : ""}`);
    }

    if ("contractUnit" in component && component.contractUnit) {
      details.push(`Contract: ${component.contractUnit}${component.contractVolume !== null && component.contractVolume !== undefined ? ` ${component.contractVolume}` : ""}`);
    }

    return `${component.componentType} → ` + details.join(" | ");
  }

  function toUniqueTreeKey(label: string, uniqueId: string) {
    return `${label}__${uniqueId}`;
  }

  function fromUniqueTreeKey(value: string) {
    const separatorIndex = value.lastIndexOf("__");
    return separatorIndex >= 0 ? value.substring(0, separatorIndex) : value;
  }

  function openAddModal() {
    setModalMode("add");
    setModalStrategy(createEmptyBudgetPricingStrategy());
    setModalOpen(true);
  }

  function openEditModal(strategy: BudgetPricingStrategyModel) {
    setModalMode("edit");
    setModalStrategy({
      ...strategy,
      components: strategy.components?.map((component) => ({ ...component })),
      positionStrategies: (strategy.positionStrategies ?? []).map((positionStrategy) => ({
        ...positionStrategy,
        components: positionStrategy.components.map((component) => ({ ...component })),
      })),
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setModalStrategy(null);
  }

  async function saveModalStrategy(strategy: BudgetPricingStrategyModel) {
    setIsSubmitting(true);

    savePricingStrategy(strategy)
      .then(updatedStrategy => {
        setBudgetPricingStrategies((previous) => {
          const existingIndex = previous.findIndex(
            (x) => x.budgetPricingStrategyId === updatedStrategy.budgetPricingStrategyId,
          );

          if (existingIndex >= 0) {
            return previous.map((item, index) => (index === existingIndex ? updatedStrategy : item));
          }

          return [...previous, updatedStrategy];
        });

        enqueueSnackbar(modalMode === "add" ? "Pricing strategy added." : "Pricing strategy updated.", { variant: "success" });
        setIsSubmitting(false);
        closeModal();
      })
      .catch(() => {
        enqueueSnackbar("Failed to save pricing strategy.", { variant: "error" });
        setIsSubmitting(false);
      });
  }

  async function deleteStrategy(budgetPricingStrategyId: number) {
    try {
      setIsSubmitting(true);
      await CommodityManagementApi.deletePricingStrategy(undefined, budgetPricingStrategyId.toString());
      await refreshPricingStrategies();
      enqueueSnackbar("Pricing strategy deleted.", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to delete pricing strategy.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  const rows = useMemo(() => {
    const mappedRows: PricingStrategyGridRow[] = [];

    for (const budgetStrategy of budgetPricingStrategies) {
      const budgetPath = [toUniqueTreeKey(budgetStrategy.name, `budget-${budgetStrategy.budgetPricingStrategyId}`)];
      const matchingPositionStrategies = [...(budgetStrategy.positionStrategies ?? [])].sort((a, b) => a.name.localeCompare(b.name));

      mappedRows.push({
        id: `budget-${budgetStrategy.budgetPricingStrategyId}`,
        path: budgetPath,
        rowType: "budget",
        budgetPricingStrategyId: budgetStrategy.budgetPricingStrategyId,
        name: budgetStrategy.name,
        commodityLabel: getScopeLabel(budgetStrategy.commodityId, commodityNameById, "All commodities"),
        locationLabel: getScopeLabel(budgetStrategy.locationId, locationNameById, "All locations"),
        counterpartyLabel: getScopeLabel(budgetStrategy.counterpartyId, counterpartyNameById, "All counterparties"),
        bushelUnit: budgetStrategy.bushelUnit ?? "",
        bushelVolume: budgetStrategy.bushelVolume?.toString() ?? "",
        contractUnit: budgetStrategy.contractUnit ?? "",
        contractVolume: budgetStrategy.contractVolume?.toString() ?? "",
        electricityType: budgetStrategy.electricityType ?? "",
        componentDetails: budgetStrategy.components?.map((x) => getComponentDetails(x, marketViewProductNameById)),
      });

      if (!usesSeparatePricingStrategies) {
        continue;
      }

      for (const positionStrategy of matchingPositionStrategies) {
        mappedRows.push({
          id: `position-${positionStrategy.positionPricingStrategyId}`,
          path: [...budgetPath, toUniqueTreeKey(positionStrategy.name, `position-${positionStrategy.positionPricingStrategyId}`)],
          rowType: "position",
          budgetPricingStrategyId: budgetStrategy.budgetPricingStrategyId,
          positionPricingStrategyId: positionStrategy.positionPricingStrategyId,
          name: positionStrategy.name,
          commodityLabel: getScopeLabel(positionStrategy.commodityId, commodityNameById, "All commodities"),
          locationLabel: getScopeLabel(positionStrategy.locationId, locationNameById, "All locations"),
          counterpartyLabel: getScopeLabel(positionStrategy.counterpartyId, counterpartyNameById, "All counterparties"),
          bushelUnit: "",
          bushelVolume: "",
          contractUnit: "",
          contractVolume: "",
          electricityType: positionStrategy.electricityType ?? "",
          componentDetails: positionStrategy.components?.map((x) => getComponentDetails(x, marketViewProductNameById)),
        });
      }
    }

    return mappedRows;
  }, [
    budgetPricingStrategies,
    commodityNameById,
    counterpartyNameById,
    locationNameById,
    marketViewProductNameById,
    usesSeparatePricingStrategies,
  ]);

  const maxComponents = useMemo(() => rows.reduce((max, row) => Math.max(max, row.componentDetails.length), 0), [rows]);

  const showBushelUnitColumn = useMemo(() => rows.some((x) => x.bushelUnit !== ""), [rows]);
  const showBushelVolumeColumn = useMemo(() => rows.some((x) => x.bushelVolume !== ""), [rows]);
  const showContractUnitColumn = useMemo(() => rows.some((x) => x.contractUnit !== ""), [rows]);
  const showContractVolumeColumn = useMemo(() => rows.some((x) => x.contractVolume !== ""), [rows]);
  const showElectricityTypeColumn = useMemo(() => rows.some((x) => x.electricityType !== ""), [rows]);

  const columns = useMemo<(ColDef<PricingStrategyGridRow> | ColGroupDef<PricingStrategyGridRow>)[]>(() => {
    const columnDefs: (ColDef<PricingStrategyGridRow> | ColGroupDef<PricingStrategyGridRow>)[] = [
      {
        headerName: "Type",
        field: "rowType",
        valueGetter: (params) => (params.data?.rowType === "budget" ? "Budget" : "Position"),
        width: 100,
      },
      {
        headerName: "Commodity",
        field: "commodityLabel",
        minWidth: 120,
        flex: 1,
      },
    ];

    if (showLocationColumn) {
      columnDefs.push({
        headerName: "Location",
        field: "locationLabel",
        minWidth: 120,
        flex: 1,
      });
    }

    if (showCounterpartyColumn) {
      columnDefs.push({
        headerName: "Counterparty",
        field: "counterpartyLabel",
        minWidth: 140,
        flex: 1,
      });
    }

    if (showBushelUnitColumn) {
      columnDefs.push({
        headerName: "Bushel Unit",
        field: "bushelUnit",
        width: 100,
      });
    }

    if (showBushelVolumeColumn) {
      columnDefs.push({
        headerName: "Bushel Volume",
        field: "bushelVolume",
        width: 100,
      });
    }

    if (showContractUnitColumn) {
      columnDefs.push({
        headerName: "Contract Unit",
        field: "contractUnit",
        width: 100,
      });
    }

    if (showContractVolumeColumn) {
      columnDefs.push({
        headerName: "Contract Volume",
        field: "contractVolume",
        width: 100,
      });
    }

    if (showElectricityTypeColumn) {
      columnDefs.push({
        headerName: "Electricity Type",
        field: "electricityType",
        width: 140,
      });
    }

    for (let componentIndex = 0; componentIndex < maxComponents; componentIndex++) {
      columnDefs.push({
        headerName: `Component ${componentIndex + 1}`,
        children: [
          {
            headerName: "Details",
            valueGetter: (params) => params.data?.componentDetails?.[componentIndex] ?? "",
            tooltipValueGetter: (params) => params.data?.componentDetails?.[componentIndex] ?? "",
            flex: 2,
          },
        ],
      });
    }

    columnDefs.push({
      headerName: "Actions",
      width: 180,
      pinned: "right",
      cellRenderer: (params: any) => {
        if (params?.data?.rowType !== "budget") {
          return null;
        }

        return (
          <ButtonGroup>
            <Button variant="text" onClick={() => {
              const strategy = budgetPricingStrategies.find((x) => x.budgetPricingStrategyId === params.data.budgetPricingStrategyId) ?? null;
              if (strategy !== null) {
                openEditModal(strategy);
              }
            }}>
              Edit
            </Button>
            <Button
              variant="text"
              color="error"
              onClick={() => deleteStrategy(params.data.budgetPricingStrategyId)}
            >
              Delete
            </Button>
          </ButtonGroup>
        );
      },
    });

    return columnDefs;
  }, [
    budgetPricingStrategies,
    isSubmitting,
    maxComponents,
    showBushelUnitColumn,
    showBushelVolumeColumn,
    showContractUnitColumn,
    showContractVolumeColumn,
    showElectricityTypeColumn,
    showCounterpartyColumn,
    showLocationColumn,
    usesSeparatePricingStrategies,
  ]);

  const gridOptions = useMemo(() => {
    return {
      treeData: true,
      groupDefaultExpanded: -1,
      getDataPath: (row: PricingStrategyGridRow) => row.path,
      autoGroupColumnDef: {
        headerName: "Strategy",
        minWidth: 280,
      },
    } as AgGridReactProps;
  }, []);

  return (
    <>
      <StoneXRow align="baseline">
        <h3>Pricing Strategies</h3>
        <Button className="pull-right" variant="outlined" onClick={openAddModal}>
          Add
        </Button>
      </StoneXRow>

      <StoneXLoading show={loadingState.isLoading()} />

      {loadingState.failed() && <div>Failed to load pricing strategies.</div>}

      {!loadingState.isLoading() && (
        <StoneXGrid
          rows={rows}
          columns={columns as ColDef<PricingStrategyGridRow>[]}
          gridOptions={gridOptions}
          getRowId={(params) => params.data.id}
          groupCellInnerRenderer={(params) => {
            const value = typeof params.value === "string" ? params.value : "";
            return fromUniqueTreeKey(value);
          }}
        />
      )}

      <StoneXModal
        header={modalMode === "add" ? "Add Pricing Strategy" : "Edit Pricing Strategy"}
        open={modalOpen && modalStrategy !== null}
        onClose={closeModal}
      >
        {modalStrategy !== null && (
          <PricingStrategyEditor
            strategy={modalStrategy}
            customerData={customerData}
            customerSettings={customerSettings}
            marketViewProducts={marketViewProducts}
            usesSeperatePricingStrategies={usesSeparatePricingStrategies}
            onCancel={closeModal}
            onSave={saveModalStrategy}
            onChange={setModalStrategy}
            isWorking={isSubmitting}
          />
        )}
      </StoneXModal>
    </>
  );
}
